import { useEffect } from "react";

/**
 * Lightweight meta-tag updater for SPAs.
 * Updates <title>, description, canonical, Open Graph and Twitter tags on mount.
 * Restores the previous <title> on unmount so navigating back returns to defaults.
 */
export default function useSeo({
  title,
  description,
  url,
  image,
  type = "article",
  keywords,
  jsonLd, // object or array
}) {
  useEffect(() => {
    const prevTitle = document.title;

    if (title) document.title = title;

    const setMeta = (attr, name, content) => {
      if (!content) return null;
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      return el;
    };

    const setLink = (rel, href) => {
      if (!href) return null;
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
      return el;
    };

    const created = [];
    created.push(setMeta("name", "description", description));
    created.push(setMeta("name", "keywords", keywords));
    created.push(setLink("canonical", url));
    created.push(setMeta("property", "og:title", title));
    created.push(setMeta("property", "og:description", description));
    created.push(setMeta("property", "og:url", url));
    created.push(setMeta("property", "og:type", type));
    created.push(setMeta("property", "og:image", image));
    created.push(setMeta("name", "twitter:title", title));
    created.push(setMeta("name", "twitter:description", description));
    created.push(setMeta("name", "twitter:image", image));

    let script;
    if (jsonLd) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "seo-jsonld-dynamic";
      script.text = JSON.stringify(jsonLd);
      // Remove any previous dynamic block
      const prev = document.getElementById("seo-jsonld-dynamic");
      if (prev) prev.remove();
      document.head.appendChild(script);
    }

    return () => {
      document.title = prevTitle;
      if (script && script.parentNode) script.parentNode.removeChild(script);
    };
  }, [title, description, url, image, type, keywords, JSON.stringify(jsonLd)]);
}
